import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { IonAccordion, IonAccordionGroup, IonButton, IonIcon, IonItem } from '@ionic/react';
import { calendarOutline, locationOutline, peopleOutline, star } from 'ionicons/icons';
import moment from 'moment';
import Loading from '../../../components/spinner';
import { withTranslation } from '../../../lib/translate';
import Layout from '../../../components/layout';
import { NormalText, SmallText, Spacer, StrongText, Title } from '../../../components/common';
import NoData from '../../../components/noData';
import Slider, { Slide } from '../../../components/slider';
import Basket from '../../../lib/basket';
import { forwardTo } from '../../../lib/utils';
import { getEvent, setEvent } from '../../../store/actions';
import './index.css';

class EventDetails extends React.Component {
    componentDidMount() {
        const id = this.props.location?.state?.id;
        if (id) {
            // drop the previously viewed event so a failed fetch can't render stale data
            this.props.dispatch(setEvent(null));
            this.props.dispatch(getEvent(id));
        }
    }

    backHandler = () => forwardTo('/events');

    getEventImages = (event) =>
        (event.images || []).slice().sort((a, b) => a.sort_order - b.sort_order);

    formatDateTime = (event) => {
        const start = moment(event.start_time);
        const end = event.end_time ? moment(event.end_time) : null;
        let datePart;
        if (end && !start.isSame(end, 'day')) {
            datePart = start.isSame(end, 'month')
                ? `${start.format('MMMM D')}–${end.format('D, YYYY')}`
                : `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
        } else {
            datePart = start.format('LL');
        }
        const timePart = end
            ? `${start.format('h:mm A')} – ${end.format('h:mm A')}`
            : start.format('h:mm A');
        return `${datePart} · ${timePart}`;
    };

    getDuration = (event) => {
        if (!event.end_time) return null;
        const start = moment(event.start_time);
        const end = moment(event.end_time);
        const days = end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1;
        if (days > 1) return `${days} Days`;
        const hours = Math.round(moment.duration(end.diff(start)).asHours());
        return `${hours} Hours`;
    };

    getAgeRestriction = (event) => {
        const tag = (event.tags || []).find((t) => /\d+\s*\+/.test(t.name));
        return tag ? tag.name : null;
    };


    getStartingPrice = (event) => {
        const prices = (event.ticket_tiers || [])
            .filter((tier) => tier.is_active)
            .map((tier) => tier.price);
        return prices.length > 0 ? Math.min(...prices) / 100 : null;
    };

    isSoldOut = (event) => {
        const tiers = (event.ticket_tiers || []).filter((tier) => tier.is_active);
        return tiers.length === 0 || tiers.every((tier) => tier.is_sold_out);
    };

    buyTickets = () => {
        if (this.isSoldOut(this.props.event)) {
            return;
        }
        Basket.reset(true);
        Basket.setEvent(true);

        forwardTo('/event-tickets', { event: this.props.event });
    };

    render() {
        const { __, event } = this.props;

        if (!event) {
            return (
                <Loading>
                    <Layout color="transparent" backHandler={this.backHandler} >
                        <div className="absolute-content flex-row-wrapper" style={{ paddingTop: 'var(--ion-padding-page)' }}>
                            <NoData label="Event not found" />
                        </div>
                    </Layout>
                </Loading>
            );
        }

        const images = this.getEventImages(event);
        const description = event.description || '';
        const startingPrice = this.getStartingPrice(event);
        const soldOut = this.isSoldOut(event);
        const faqs = event.faqs || [];
        const details = [
            { label: 'Venue', value: event.venue_name },
            { label: 'Duration', value: this.getDuration(event) },
            { label: 'Age restriction', value: this.getAgeRestriction(event) },
            { label: 'Category', value: event.category?.name },
        ].filter((detail) => detail.value);

        return (
            <Loading transparent>
                <Layout color="transparent" backHandler={this.backHandler} >
                    <div className="event-details">
                        {images.length > 0 && (
                            <Slider
                                className="event-details-slider"
                                settings={{
                                    slidesPerView: 1,
                                    loop: images.length > 1,
                                    pagination: {
                                        el: '.swiper-pagination',
                                        clickable: true,
                                    },
                                }}
                            >
                                {images.map((image, i) => (
                                    <Slide key={`event-image-${image.id ?? i}`}>
                                        <img src={image.image} alt={image.alt_text} />
                                    </Slide>
                                ))}
                                <div className="swiper-pagination"></div>
                            </Slider>
                        )}

                        <div className="event-details-data">
                            <Title className="event-details-name">{event.name}</Title>

                            <div className="event-details-meta">
                                <IonIcon icon={calendarOutline} />
                                <SmallText>{this.formatDateTime(event)}</SmallText>
                            </div>
                            <div className="event-details-meta">
                                <IonIcon icon={locationOutline} />
                                <SmallText>
                                    {[event.venue_name, event.venue_address].filter(Boolean).join(', ')}
                                </SmallText>
                            </div>

                            {(event.attending > 0 || event.rating > 0) && (
                                <>
                                    <div className="event-details-pills">
                                        {event.attending > 0 && (
                                            <div className="event-details-pill">
                                                <IonIcon className='fill-label' icon={peopleOutline} />
                                                <SmallText>{`${event.attending.toLocaleString()} ${__('attending')}`}</SmallText>
                                            </div>
                                        )}
                                        {event.rating > 0 && (
                                            <div className="event-details-pill">
                                                <IonIcon icon={star} className="event-details-star" />
                                                <SmallText>{event.rating}</SmallText>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {description && (
                                <>
                                    <Spacer size={2} />

                                    <StrongText className="block">
                                        {__('About event')}
                                    </StrongText>
                                    <Spacer size={0} />
                                    <NormalText className="block event-details-description">
                                        {description}
                                    </NormalText>
                                    <Spacer size={2} />

                                </>
                            )}

                            {details.length > 0 && (
                                <div className="event-details-grid box-content">
                                    {details.map((detail) => (
                                        <div className="event-details-grid-item" key={detail.label}>
                                            <SmallText className="block event-details-grid-label">
                                                {__(detail.label)}
                                            </SmallText>
                                            <SmallText className="block bold  label-color">
                                                {__(detail.value)}
                                            </SmallText>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {faqs.length > 0 && (
                                <>
                                    <Spacer size={4} />
                                    <StrongText className="block event-details-section-title">
                                        {__('Frequently asked questions')}
                                    </StrongText>
                                    <Spacer size={1} />
                                    <IonAccordionGroup className="event-details-faq">
                                        {faqs.map((faq, i) => (
                                            <IonAccordion key={`faq-${i}`} value={`faq-${i}`}>
                                                <IonItem slot="header" lines="none">
                                                    <NormalText className='label-color'>{__(faq.question)}</NormalText>
                                                </IonItem>
                                                <div slot="content" className="event-details-faq-answer">
                                                    <SmallText>{__(faq.answer)}</SmallText>
                                                </div>
                                            </IonAccordion>
                                        ))}
                                    </IonAccordionGroup>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="event-details-footer">
                        <IonButton
                            expand="block"
                            disabled={soldOut}
                            onClick={this.buyTickets}
                        >
                            {soldOut
                                ? __('Sold out')
                                : startingPrice !== null
                                    ? __('Get Tickets · From {price}', {
                                        price: Basket.formatPrice(startingPrice, true),
                                    })
                                    : __('Get Tickets')}
                        </IonButton>
                    </div>
                </Layout>
            </Loading>
        );
    }
}

const stateToProps = (store) => {
    const { event } = store.orders;
    return {
        event,
    };
};

export default connect(stateToProps)(withRouter(withTranslation(EventDetails)));
